import download from "downloadjs";
import { PDFDocument, StandardFonts, rgb } from "pdf-lib";
import moment from "moment";

import klerpLogoPng from "../../assets/fonts/klerp-logo.png";

const NAVY = rgb(0.106, 0.165, 0.255);
const GOLD = rgb(0.675, 0.541, 0.302);
const LIGHT_PANEL = rgb(0.93, 0.94, 0.96);
const MUTED = rgb(0.38, 0.38, 0.42);
const LINE = rgb(0.82, 0.84, 0.88);
const STRIPE = rgb(0.965, 0.975, 0.99);
const WHITE = rgb(1, 1, 1);


const createProposal = async (order) => {
    try {
        const pdfDoc = await PDFDocument.create();
        const timesRoman = await pdfDoc.embedFont(StandardFonts.TimesRoman);
        const timesRomanBold = await pdfDoc.embedFont(StandardFonts.TimesRomanBold);
        const helvetica = await pdfDoc.embedFont(StandardFonts.Helvetica);
        const helveticaBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
        const margin = 50;

        const customerName = order.userId?.name || order.address || "Customer";

        const page1 = pdfDoc.addPage();
        const { width: pageWidth, height: pageHeight } = page1.getSize();
        const lineMargin = 30;
        const contentRight = pageWidth - 30;

        let logoImage = null;
        try {
            const logoImageBytes = await fetch(klerpLogoPng).then((res) => res.arrayBuffer());
            logoImage = await pdfDoc.embedPng(new Uint8Array(logoImageBytes));
        } catch (_) {
            logoImage = null;
        }

        const drawPageLines = (page) => {
            page.drawRectangle({ x: 0, y: pageHeight - 5, width: pageWidth, height: 5, color: NAVY });
            page.drawText("KLERP TECH SDN BHD", {
                x: lineMargin,
                y: pageHeight - 22,
                size: 8,
                font: timesRomanBold,
                color: NAVY,
            });
            const runningTitle = "PROPOSAL";
            page.drawText(runningTitle, {
                x: contentRight - timesRomanBold.widthOfTextAtSize(runningTitle, 8),
                y: pageHeight - 22,
                size: 8,
                font: timesRomanBold,
                color: NAVY,
            });
            page.drawLine({
                start: { x: lineMargin, y: pageHeight - 28 },
                end: { x: contentRight, y: pageHeight - 28 },
                thickness: 0.4,
                color: LINE,
            });
        };

        const coverLeft = 46;

        page1.drawRectangle({
            x: 0,
            y: 0,
            width: pageWidth,
            height: pageHeight,
            color: WHITE,
        });

        const drawRing = (x, yPos, radius, borderWidth, borderOpacity) => {
            page1.drawCircle({
                x,
                y: yPos,
                size: radius,
                borderColor: GOLD,
                borderWidth,
                borderOpacity,
            });
        };
        drawRing(-36, pageHeight + 28, 108, 8, 0.9);
        drawRing(-8, pageHeight - 6, 168, 2.5, 0.45);
        drawRing(pageWidth + 24, -36, 196, 9, 0.85);
        drawRing(pageWidth - 18, 78, 112, 2.5, 0.4);

        if (logoImage) {
            const coverLogo = logoImage.scale(0.16);
            page1.drawImage(logoImage, {
                x: pageWidth - 40 - coverLogo.width,
                y: pageHeight - 42 - coverLogo.height,
                width: coverLogo.width,
                height: coverLogo.height,
            });
        } else {
            const brand = "KLERP TECH";
            page1.drawText(brand, {
                x: pageWidth - 46 - helveticaBold.widthOfTextAtSize(brand, 14),
                y: pageHeight - 64,
                size: 14,
                font: helveticaBold,
                color: NAVY,
            });
        }

        const titleSize = 52;
        const titleY = 430;
        page1.drawText("PROJECT", {
            x: coverLeft,
            y: titleY,
            size: titleSize,
            font: helveticaBold,
            color: NAVY,
        });
        page1.drawText("PROPOSAL", {
            x: coverLeft,
            y: titleY - 58,
            size: titleSize,
            font: helveticaBold,
            color: NAVY,
        });

        const subtitleMax = 360;
        const subtitleSize = 18;
        const subtitleWords = String(customerName).split(/\s+/).filter(Boolean);
        const subtitleLines = [];
        let subtitleLine = "";
        subtitleWords.forEach((word) => {
            const next = subtitleLine ? `${subtitleLine} ${word}` : word;
            if (helvetica.widthOfTextAtSize(next, subtitleSize) > subtitleMax && subtitleLine) {
                subtitleLines.push(subtitleLine);
                subtitleLine = word;
            } else {
                subtitleLine = next;
            }
        });
        if (subtitleLine) subtitleLines.push(subtitleLine);
        const shownSubtitleLines = subtitleLines.slice(0, 2);
        shownSubtitleLines.forEach((line, index) => {
            page1.drawText(line, {
                x: coverLeft,
                y: titleY - 102 - index * 24,
                size: subtitleSize,
                font: helvetica,
                color: rgb(0.28, 0.34, 0.42),
            });
        });

        const createdMoment = order.createdAt ? moment(order.createdAt) : null;
        if (createdMoment?.isValid()) {
            const coverDate = createdMoment.format("D MMMM YYYY");
            page1.drawText(coverDate, {
                x: coverLeft,
                y: titleY - 102 - shownSubtitleLines.length * 24 - 6,
                size: 13,
                font: helvetica,
                color: rgb(0.28, 0.34, 0.42),
            });
        }

        const contactLines = [
            { text: "KLERP TECH SDN BHD", font: timesRomanBold, size: 11, color: NAVY },
            { text: "C3-3-13A, Solaris Dutamas", font: timesRoman, size: 11, color: rgb(0.1, 0.12, 0.16) },
            { text: "50450 Kuala Lumpur", font: timesRoman, size: 11, color: rgb(0.1, 0.12, 0.16) },
            { text: "+6 012 5488 100", font: timesRoman, size: 11, color: rgb(0.1, 0.12, 0.16) },
            { text: "www.klerptech.com", font: timesRoman, size: 11, color: rgb(0.1, 0.12, 0.16) },
        ];
        contactLines.forEach((line, index) => {
            page1.drawText(line.text, {
                x: coverLeft,
                y: 136 - index * 16,
                size: line.size,
                font: line.font,
                color: line.color,
            });
        });

        // —— Page 2: Workplan ——
        const page2 = pdfDoc.addPage();
        drawPageLines(page2);
        const contentMargin = lineMargin;
        const topContentGap = 55;
        let y = pageHeight - margin - topContentGap;

        const scopeTitle = `${customerName} SYSTEM DEVELOPMENT`.toUpperCase();
        const scopeTitleMax = pageWidth - contentMargin * 2;
        let scopeTitleSize = 14;
        while (scopeTitleSize > 10 && timesRomanBold.widthOfTextAtSize(scopeTitle, scopeTitleSize) > scopeTitleMax) {
            scopeTitleSize -= 0.5;
        }
        page2.drawText(scopeTitle, { x: contentMargin, y, size: scopeTitleSize, font: timesRomanBold, color: NAVY });
        y -= 26;

        const timelineLeft = contentMargin;
        const timelineRight = pageWidth - contentMargin;
        const timelineWidth = timelineRight - timelineLeft;
        const timelineRowHeight = 22;

        page2.drawRectangle({
            x: timelineLeft,
            y: y - 5,
            width: timelineWidth,
            height: 18,
            color: NAVY,
        });
        page2.drawText("MILESTONE", { x: timelineLeft + 8, y, size: 9, font: timesRomanBold, color: WHITE });
        page2.drawText("DUE DATE", {
            x: timelineRight - 8 - timesRomanBold.widthOfTextAtSize("DUE DATE", 9),
            y,
            size: 9,
            font: timesRomanBold,
            color: WHITE,
        });
        y -= 24;

        const workplanItems = Array.isArray(order.milestones) && order.milestones.length > 0
            ? order.milestones
            : [{ modules: "—", dueDate: "—" }];
        const formatAmount = (value) => Math.round(Number(value) || 0).toLocaleString("en-MY");
        const addonUnit = 8000;
        const addonIndexes = new Set([17, 18, 19]);
        let addonHeaderDrawn = false;

        workplanItems.forEach((item, index) => {
            const isAddon = addonIndexes.has(index);
            if (isAddon && !addonHeaderDrawn) {
                addonHeaderDrawn = true;
                y -= 10;
                page2.drawRectangle({
                    x: timelineLeft,
                    y: y - 5,
                    width: timelineWidth,
                    height: 18,
                    color: NAVY,
                });
                page2.drawText("OPTIONAL ADD-ONS", { x: timelineLeft + 8, y, size: 9, font: timesRomanBold, color: WHITE });
                y -= 24;
            }
            const modulesStr = String(item.modules ?? "").trim() || "—";
            const dueDateStr = isAddon
                ? `RM ${formatAmount(addonUnit)}`
                : (String(item.dueDate ?? "").trim() || "—");
            if (index % 2 === 1) {
                page2.drawRectangle({
                    x: timelineLeft,
                    y: y - 6,
                    width: timelineWidth,
                    height: timelineRowHeight,
                    color: STRIPE,
                });
            }
            const number = String(index + 1).padStart(2, "0");
            page2.drawText(number, { x: timelineLeft + 8, y, size: 9, font: timesRomanBold, color: NAVY });
            page2.drawText(modulesStr, { x: timelineLeft + 32, y, size: 11, font: timesRoman, color: rgb(0, 0, 0) });
            page2.drawText(dueDateStr, {
                x: timelineRight - 8 - timesRomanBold.widthOfTextAtSize(dueDateStr, 11),
                y,
                size: 11,
                font: timesRomanBold,
                color: NAVY,
            });
            y -= timelineRowHeight;
        });

        const productsList = Array.isArray(order.products) && order.products.length > 0 ? order.products : [];
        const costTotal = productsList.reduce((sum, product) => sum + (product.price != null ? Number(product.price) : 0), 0);
        const addonCount = workplanItems.filter((_, index) => addonIndexes.has(index)).length;
        const quotedTotal = order.price != null ? Number(order.price) : costTotal;
        const totalAmount = Math.max(0, quotedTotal - addonCount * addonUnit);
        const totalStr = `RM ${formatAmount(totalAmount)}`;
        const totalLabel = "TOTAL";
        const totalQualifier = "(without optional add-ons)";
        const totalLabelSize = 13;
        const totalQualifierSize = 11;
        const totalPad = 16;
        const totalLabelGap = 8;
        const totalLabelWidth = timesRomanBold.widthOfTextAtSize(totalLabel, totalLabelSize);
        const totalQualifierWidth = timesRoman.widthOfTextAtSize(totalQualifier, totalQualifierSize);
        const totalBoxWidth = Math.max(220, totalLabelWidth + totalLabelGap + totalQualifierWidth + totalPad * 2);
        const totalBoxHeight = 64;
        const totalBoxX = timelineLeft;
        const totalBoxTop = y - 36;
        const totalBoxBottom = totalBoxTop - totalBoxHeight;
        page2.drawRectangle({
            x: totalBoxX,
            y: totalBoxBottom,
            width: totalBoxWidth,
            height: totalBoxHeight,
            color: LIGHT_PANEL,
            borderColor: LINE,
            borderWidth: 0.6,
        });
        const totalLabelY = totalBoxTop - 22;
        page2.drawText(totalLabel, {
            x: totalBoxX + totalPad,
            y: totalLabelY,
            size: totalLabelSize,
            font: timesRomanBold,
            color: MUTED,
        });
        page2.drawText(totalQualifier, {
            x: totalBoxX + totalPad + totalLabelWidth + totalLabelGap,
            y: totalLabelY + 1,
            size: totalQualifierSize,
            font: timesRoman,
            color: MUTED,
        });
        page2.drawText(totalStr, {
            x: totalBoxX + totalPad,
            y: totalBoxTop - 46,
            size: 16,
            font: timesRomanBold,
            color: NAVY,
        });

        const monthlyFeeAmount = Number(order.monthlyFees);
        const hasMonthlyFee = Number.isFinite(monthlyFeeAmount) && monthlyFeeAmount > 0;
        const monthlyFeeStr = `RM${formatAmount(monthlyFeeAmount)}`;
        const maintenanceNote = hasMonthlyFee
            ? `An optional monthly hosting fee of ${monthlyFeeStr} is available once the due dates above have been met, or upon completion of the project, should the client require KLERP TECH to host and manage the server and domain. The fee includes server hosting, support, maintenance, and fixes for any issues within the scope of work set out in the milestone above.`
            : "";
        const noteSize = 11;
        const noteMaxWidth = timelineWidth;
        const noteWords = maintenanceNote.split(/\s+/).filter(Boolean);
        const noteLines = [];
        let noteLine = "";
        noteWords.forEach((word) => {
            const next = noteLine ? `${noteLine} ${word}` : word;
            if (timesRoman.widthOfTextAtSize(next, noteSize) > noteMaxWidth && noteLine) {
                noteLines.push(noteLine);
                noteLine = word;
            } else {
                noteLine = next;
            }
        });
        if (noteLine) noteLines.push(noteLine);
        let noteY = totalBoxBottom - 22;
        noteLines.forEach((line, index) => {
            const feeLead = `An optional monthly hosting fee of ${monthlyFeeStr}`;
            if (index === 0 && line.startsWith(feeLead)) {
                page2.drawText(feeLead, {
                    x: timelineLeft,
                    y: noteY,
                    size: noteSize,
                    font: timesRomanBold,
                    color: NAVY,
                });
                const rest = line.slice(feeLead.length);
                page2.drawText(rest, {
                    x: timelineLeft + timesRomanBold.widthOfTextAtSize(feeLead, noteSize),
                    y: noteY,
                    size: noteSize,
                    font: timesRoman,
                    color: rgb(0.1, 0.12, 0.16),
                });
            } else {
                page2.drawText(line, {
                    x: timelineLeft,
                    y: noteY,
                    size: noteSize,
                    font: timesRoman,
                    color: rgb(0.1, 0.12, 0.16),
                });
            }
            noteY -= 16;
        });

        let summaryPage = pdfDoc.addPage();
        drawPageLines(summaryPage);
        y = pageHeight - margin - topContentGap;

        summaryPage.drawText("EXECUTIVE SUMMARY & SCOPE OF WORK", {
            x: contentMargin,
            y,
            size: 16,
            font: timesRomanBold,
            color: NAVY,
        });
        y -= 30;

        const objectivesList = Array.isArray(order.objectives) && order.objectives.length > 0
            ? order.objectives
            : ["No objectives defined."];
        const objSize = 12;
        const objLeading = 18;
        const objBottom = 48;
        const summaryLeft = contentMargin;
        const summaryRight = pageWidth - contentMargin;
        const summaryTextX = summaryLeft + 36;

        const continueSummaryPage = () => {
            summaryPage = pdfDoc.addPage();
            drawPageLines(summaryPage);
            y = pageHeight - margin - topContentGap;
        };

        const wrapToWidth = (text, font, maxWidth) => {
            const words = String(text).trim().split(/\s+/).filter(Boolean);
            if (words.length === 0) return ["—"];
            const lines = [];
            let line = "";
            words.forEach((word) => {
                const next = line ? `${line} ${word}` : word;
                if (font.widthOfTextAtSize(next, objSize) > maxWidth && line) {
                    lines.push(line);
                    line = word;
                } else {
                    line = next;
                }
            });
            if (line) lines.push(line);
            return lines;
        };

        const summaryBlocks = [];
        const pushObjective = (raw) => {
            const text = String(raw || "").replace(/\r\n/g, "\n").replace(/\*\*/g, "").trim();
            if (!text) return;
            const hasMarks = /#{1,6}\s*/.test(text) || /\(\d+\)\s+/.test(text) || /(?:^|\n)\s*\d+[.)]\s+/.test(text);
            if (!hasMarks) {
                summaryBlocks.push({ type: "item", text: text.replace(/\s+/g, " ").trim() || "—" });
                return;
            }
            const sections = (/#{1,6}/.test(text) ? text.split(/\s*#{1,6}\s*/) : [text])
                .map((section) => section.trim())
                .filter(Boolean);
            sections.forEach((section) => {
                const pieces = section
                    .split(/\s*(?=\(\d+\)\s+|(?:^|\n)\s*\d+[.)]\s+)/)
                    .map((piece) => piece.trim())
                    .filter(Boolean);
                if (pieces.length === 0) return;
                const firstIsItem = /^(?:\(\d+\)|\d+[.)])\s+/.test(pieces[0]);
                let start = 0;
                if (!firstIsItem) {
                    const heading = pieces[0].replace(/\s+/g, " ").trim();
                    if (heading) summaryBlocks.push({ type: "heading", text: heading });
                    start = 1;
                }
                for (let index = start; index < pieces.length; index += 1) {
                    const numbered = pieces[index].match(/^(?:(\(\d+\)|\d+[.)])\s+)([\s\S]+)$/);
                    if (numbered) {
                        summaryBlocks.push({
                            type: "item",
                            label: `(${numbered[1].replace(/\D/g, "")})`,
                            text: numbered[2].replace(/\s+/g, " ").trim(),
                        });
                    } else {
                        summaryBlocks.push({ type: "item", text: pieces[index].replace(/\s+/g, " ").trim() });
                    }
                }
            });
        };
        objectivesList.forEach(pushObjective);

        let itemCount = 0;
        summaryBlocks.forEach((block) => {
            if (block.type === "heading") {
                itemCount = 0;
                const barHeight = 28;
                const label = block.text.toUpperCase();
                const tracking = 0.55;
                let headingSize = 11;
                const headingMax = summaryRight - summaryLeft - 28;
                const trackedWidth = (text, size) => {
                    let width = 0;
                    for (const char of text) width += helveticaBold.widthOfTextAtSize(char, size) + tracking;
                    return width - tracking;
                };
                while (headingSize > 9 && trackedWidth(label, headingSize) > headingMax) {
                    headingSize -= 0.5;
                }
                if (y - barHeight - 16 < objBottom) continueSummaryPage();
                y -= 16;
                const barBottom = y - 9;
                summaryPage.drawRectangle({
                    x: summaryLeft,
                    y: barBottom,
                    width: summaryRight - summaryLeft,
                    height: barHeight,
                    color: NAVY,
                });
                const capHeight = headingSize * 0.72;
                let textX = summaryLeft + 14;
                const textY = barBottom + (barHeight - capHeight) / 2;
                for (const char of label) {
                    summaryPage.drawText(char, {
                        x: textX,
                        y: textY,
                        size: headingSize,
                        font: helveticaBold,
                        color: WHITE,
                    });
                    textX += helveticaBold.widthOfTextAtSize(char, headingSize) + tracking;
                }
                y = barBottom - 18;
                return;
            }
            itemCount += 1;
            const number = block.label ? block.label.replace(/\D/g, "") : String(itemCount);
            const lines = wrapToWidth(block.text, timesRoman, summaryRight - summaryTextX);
            lines.forEach((line, lineIndex) => {
                if (y - objLeading < objBottom) continueSummaryPage();
                if (lineIndex === 0) {
                    summaryPage.drawText(String(number).padStart(2, "0"), {
                        x: summaryLeft + 10,
                        y,
                        size: objSize,
                        font: timesRoman,
                        color: rgb(0, 0, 0),
                    });
                }
                summaryPage.drawText(line, {
                    x: summaryTextX,
                    y,
                    size: objSize,
                    font: timesRoman,
                    color: rgb(0, 0, 0),
                });
                y -= objLeading;
            });
            y -= 4;
        });

        // —— Terms & Conditions: formal letter, single column ——
        const letterLeft = contentMargin;
        const letterRight = pageWidth - contentMargin;
        let letterBottom = 48;
        const letterSize = 12;
        const letterLeading = 18;
        const bodyInk = rgb(0, 0, 0);
        let letterPage = null;
        let letterY = 0;
        let letterTight = false;
        let termsPageCount = 0;

        const newLetterPage = () => {
            letterPage = pdfDoc.addPage();
            drawPageLines(letterPage);
            letterBottom = letterTight ? 28 : 48;
            const termsContinuation = letterTight && termsPageCount > 0;
            letterY = termsContinuation ? pageHeight - 70 : pageHeight - margin - topContentGap;
            if (letterTight) termsPageCount += 1;
        };

        const measureLetter = (text, font, size = letterSize) => font.widthOfTextAtSize(text, size);

        const drawLetterHeading = (text, asTitle = false) => {
            if (asTitle) {
                if (!letterPage || letterY - 32 < letterBottom) newLetterPage();
                letterPage.drawText(text.toUpperCase(), {
                    x: letterLeft,
                    y: letterY,
                    size: 16,
                    font: timesRomanBold,
                    color: NAVY,
                });
                letterY -= 36;
                return;
            }
            const heading = text.toUpperCase();
            const headingSize = 12;
            if (!letterPage || letterY - 40 < letterBottom) newLetterPage();
            letterY -= 22;
            letterPage.drawText(heading, {
                x: letterLeft,
                y: letterY,
                size: headingSize,
                font: timesRomanBold,
                color: NAVY,
            });
            const headingWidth = timesRomanBold.widthOfTextAtSize(heading, headingSize);
            letterPage.drawLine({
                start: { x: letterLeft, y: letterY - 7 },
                end: { x: letterLeft + headingWidth, y: letterY - 7 },
                thickness: 0.7,
                color: NAVY,
            });
            letterY -= 30 + letterLeading / 2;
        };

        const drawLetterParagraph = (segments, xStart = letterLeft) => {
            const maxW = letterRight - xStart;
            const words = [];
            segments.forEach((segment) => {
                segment.text.split(/(\s+)/).filter((part) => part.length > 0).forEach((part) => {
                    words.push({
                        text: part,
                        font: segment.font,
                        color: segment.color || bodyInk,
                    });
                });
            });
            const lines = [];
            let line = [];
            let lineW = 0;
            words.forEach((word) => {
                const wordWidth = measureLetter(word.text, word.font);
                if (!word.text.trim()) {
                    if (line.length === 0 || lineW + wordWidth > maxW) return;
                    line.push(word);
                    lineW += wordWidth;
                    return;
                }
                if (lineW + wordWidth > maxW && line.length > 0) {
                    lines.push(line);
                    line = [];
                    lineW = 0;
                }
                line.push(word);
                lineW += wordWidth;
            });
            if (line.length) lines.push(line);
            return lines;
        };

        const paintLetterLines = (lines, xStart) => {
            lines.forEach((parts) => {
                if (!letterPage || letterY - letterLeading < letterBottom) newLetterPage();
                let x = xStart;
                parts.forEach((part) => {
                    letterPage.drawText(part.text, {
                        x,
                        y: letterY,
                        size: letterSize,
                        font: part.font,
                        color: part.color,
                    });
                    x += measureLetter(part.text, part.font);
                });
                letterY -= letterLeading;
            });
            letterY -= 6;
        };

        const drawLetterClause = (number, lead, body) => {
            const textX = letterLeft + 36;
            const segments = [];
            if (lead) segments.push({ text: `${lead} `, font: timesRoman, color: bodyInk });
            segments.push({ text: body, font: timesRoman, color: bodyInk });
            const lines = drawLetterParagraph(segments, textX);
            lines.forEach((parts, lineIndex) => {
                if (!letterPage || letterY - letterLeading < letterBottom) newLetterPage();
                if (lineIndex === 0) {
                    letterPage.drawText(String(number).padStart(2, "0"), {
                        x: letterLeft + 10,
                        y: letterY,
                        size: letterSize,
                        font: timesRoman,
                        color: bodyInk,
                    });
                }
                let x = textX;
                parts.forEach((part) => {
                    letterPage.drawText(part.text, {
                        x,
                        y: letterY,
                        size: letterSize,
                        font: part.font,
                        color: part.color,
                    });
                    x += measureLetter(part.text, part.font);
                });
                letterY -= letterLeading;
            });
            letterY -= 4;
        };

        const drawLetterTable = (headers, rows) => {
            const tableW = letterRight - letterLeft;
            const colW = tableW / headers.length;
            const headerH = 24;
            const rowH = 26;
            const totalH = headerH + rows.length * rowH;
            if (!letterPage || letterY - totalH < letterBottom) newLetterPage();
            const top = letterY + 11;
            letterPage.drawRectangle({
                x: letterLeft,
                y: top - headerH,
                width: tableW,
                height: headerH,
                color: NAVY,
            });
            rows.forEach((row, rowIndex) => {
                if (rowIndex % 2 === 1) {
                    letterPage.drawRectangle({
                        x: letterLeft,
                        y: top - headerH - (rowIndex + 1) * rowH + 4,
                        width: tableW,
                        height: rowH,
                        color: STRIPE,
                    });
                }
                row.forEach((cell, index) => {
                    const cellText = String(cell);
                    const cellSize = 12;
                    const cellX = index === headers.length - 1
                        ? letterLeft + (index + 1) * colW - 8 - timesRomanBold.widthOfTextAtSize(cellText, cellSize)
                        : letterLeft + index * colW + 8;
                    letterPage.drawText(cellText, {
                        x: cellX,
                        y: top - headerH - rowIndex * rowH - 15,
                        size: cellSize,
                        font: index === headers.length - 1 ? timesRomanBold : timesRoman,
                        color: index === headers.length - 1 ? NAVY : bodyInk,
                    });
                });
            });
            headers.forEach((header, index) => {
                const headerText = String(header).toUpperCase();
                const headerSize = 11;
                const headerX = index === headers.length - 1
                    ? letterLeft + (index + 1) * colW - 10 - timesRomanBold.widthOfTextAtSize(headerText, headerSize)
                    : letterLeft + index * colW + 10;
                letterPage.drawText(headerText, {
                    x: headerX,
                    y: top - 16,
                    size: headerSize,
                    font: timesRomanBold,
                    color: WHITE,
                });
            });
            letterY = top - totalH - 12;
        };

        const termsSections = [
            {
                heading: "Definitions",
                clauses: [
                    ["Agreement", "means the Project Proposal, Terms and Conditions and any other attached documents."],
                    ["Project", "means the scope and purpose of the Client's identified usage of the work product as described in the Project Proposal."],
                    ["Services", "means all services and the work product to be provided to Client by KLERP TECH as described and otherwise further defined in the Project Proposal."],
                    ["Deliverables", "means the services and work product specified in the Project Proposal to be delivered by KLERP TECH to Client."],
                    ["Client Content", "means all materials, writing, images or other creative content provided by Client used in preparing or creating the Deliverables."],
                ],
            },
            {
                heading: "Our Services",
                clauses: [
                    [null, "KLERP TECH shall perform all the services listed in the Work Plan & Milestones schedule."],
                ],
            },
            {
                heading: "Proposal",
                clauses: [
                    [null, "The terms of this agreement expires thirty (30) days after being submitted to Client. If this agreement expires, KLERP TECH may modify the agreement and resubmit it to Client."],
                ],
            },
            {
                heading: "Compensation",
                clauses: [
                    ["Fees:", "Client agrees to pay KLERP TECH the fees listed in the project proposal, including all taxes."],
                    ["Additional Costs:", "Pricing in the project proposal includes only KLERP TECH fees. Any other costs, such as hosting or art licensing will be billed to Client."],
                    ["Hosting:", "KLERP TECH will host the project for Client while the project is under construction. After the delivery of the final deliverables, KLERP TECH will cease server operations. If Client wishes KLERP TECH to maintain server operations, a separate fee will incur."],
                ],
            },
            {
                heading: "Payment",
                clauses: [
                    ["Payment Schedule:", "Payment is due in four parts: 25% as deposit, 25% on the first milestone, 25% on the second milestone, and 25% on completion."],
                    ["Invoices:", "All invoices are payable within fifteen (15) days of the invoice. Invoices shall list any expenses and additional costs as separate items."],
                ],
                table: {
                    headers: ["Payment", "Terms"],
                    rows: [
                        ["Deposit", "25%"],
                        ["First milestone", "25%"],
                        ["Second milestone", "25%"],
                        ["Completion", "25%"],
                    ],
                },
            },
            {
                heading: "Late Payment",
                clauses: [
                    ["Late Fee:", "A monthly service fee of fifteen (15) percent of the total project cost, or the maximum allowed by law, is payable on all overdue balances."],
                    ["Credit Late Payments:", "Payments will be credited to late payments first, then to unpaid balances."],
                    ["Collection Expenses:", "Client shall pay all collection or legal fees caused by late payments."],
                    ["Withholding Delivery:", "KLERP TECH may withhold delivery and transfer of ownership of any current work if accounts are not current or overdue invoices are not paid in full."],
                    ["Withholding License:", "All grants of any license to use or transfer ownership of any intellectual property rights under this agreement are conditioned on full payment, including all additional costs, expenses, fees or any other charges."],
                ],
            },
            {
                heading: "Changes to Project Scope",
                clauses: [
                    ["Change Request:", "If Client wants to change the Scope of Work after acceptance of this agreement, Client shall send KLERP TECH a written change order describing the requested changes in detail. Within five (5) days of receiving a change order, KLERP TECH will respond with a statement proposing availability, additional fees, changes to delivery dates, and any modification to the terms & conditions. KLERP TECH will evaluate each change order at its standard rate and charges."],
                ],
            },
            {
                heading: "Delays",
                clauses: [
                    ["Delays:", "KLERP TECH shall use all reasonable efforts to meet the work plan & milestones delivery schedule. KLERP TECH may extend the due date for any deliverable by giving written notice to Client. The total of all extensions shall not exceed thirty (30) days."],
                    ["Client Delays:", "Client shall use all reasonable efforts to provide needed information, materials and approvals. Any delay by Client will result in a day-for-day extension of the due date for all deliverables."],
                    ["General Delays:", "Any delay caused by conditions beyond the reasonable control of the parties shall not be considered a breach and will result in a day-to-day extension of the due date for all deliverables. Each party shall use reasonable efforts to notify the other party, in writing, of a delay. Conditions beyond the reasonable control of parties include, but are not limited to, natural disasters, acts of government after the date of agreement, power failure, fire, flood, labor disputes, acts of war or terrorism and epidemics."],
                ],
            },
            {
                heading: "Evaluation and Acceptance",
                clauses: [
                    ["Testing:", "KLERP TECH will test and correct deliverables using commercially reasonable efforts before providing deliverables to Client."],
                    ["Approval Periods:", "Client shall, within five (5) business days after receiving each deliverable, notify KLERP TECH in writing of any failure to comply with the specification of the project proposal or of any other objections, corrections, or changes required. KLERP TECH shall, within five (5) business days of receiving Clients notification, correct and submit a revised deliverable to Client. Client shall, within five (5) business days of receiving a revised deliverable, either approve the corrected version, or make further changes. If after three (3) corrections by KLERP TECH, Client finds the deliverables are not acceptable, client may terminate this agreement subject to the termination clauses of this agreement. If Client fails to provide approval or comments during any approval period, those deliverables will be considered approved and accepted. All objections, corrections and changes shall be subject to the terms and conditions of this agreement."],
                ],
            },
            {
                heading: "Client Responsibilities",
                clauses: [
                    [null, "Client acknowledges that it is responsible for performing the following in a reasonable and timely manner: (a) Provide Client content in a form suitable for use in the Deliverables without further preparation by KLERP TECH, unless otherwise specified in the project proposal; (b) Proofread all deliverables. Client will be charged for correcting errors after the acceptance of any deliverable; (c) Make decisions regarding other parties."],
                ],
            },
            {
                heading: "Accreditation",
                clauses: [
                    ["Accreditation:", "KLERP TECH retains the right to reproduce, publish and display the deliverables in KLERP TECH's portfolios and websites for the purposes of recognition of professional advancement, and to be credited with authorship of the deliverables in connection with such uses."],
                ],
            },
            {
                heading: "Confidential Information",
                clauses: [
                    [null, "Client's 'confidential information' includes information that KLERP TECH should reasonably believe to be confidential. KLERP TECH's 'confidential information' includes the source codes related to the project. All material considered confidential by either party shall be designated as confidential. Confidential information shall not be disclosed to third parties and shall only be used as needed to perform this agreement. Confidential information shall not include any information that is already known by the recipient, becomes publicly known through no fault of the recipient, or is received from a third party without a restriction on disclosure."],
                ],
            },
            {
                heading: "Relationship of the Parties",
                clauses: [
                    ["Independent Contractor:", "KLERP TECH is an independent contractor. KLERP TECH shall determine, in its sole discretion, the manner and means by which the services are accomplished. No agency, partnership, joint venture, or employee-employer relationship is intended or created by this agreement. Neither party is authorised to act as agent or bind the other party except as expressly stated in this agreement."],
                    ["No Exclusivity:", "This agreement does not create an exclusive relationship between the parties. Client is free to engage others to perform services of the same or similar nature to those provided by KLERP TECH, and KLERP TECH shall be entitled to offer and provide our services to others, solicit other clients and otherwise advertise the services offered by KLERP TECH."],
                ],
            },
            {
                heading: "Representations and Warranties",
                clauses: [
                    ["By Client:", "Client represents and warrants to KLERP TECH that: (a) To the best of Client's knowledge, use of the client content does not infringe any rights of any third party; (b) Client shall comply with the terms and conditions of any licensing agreements which govern the use of third party materials; (c) Client will obtain all necessary and appropriate rights and licenses to grant license to KLERP TECH to use third party materials."],
                    ["By KLERP TECH:", "KLERP TECH represents and warrants to Client that: (a) KLERP TECH will provide the services identified in the agreement in a professional and workmanlike manner; (b) KLERP TECH shall secure all necessary rights, title and interest in and to the final deliverables for KLERP TECH to grant the intellectual property rights provided in this agreement; (c) To the best of KLERP TECH's knowledge, the deliverables will not violate the rights of any third parties; (d) If Client or third parties modify the deliverables or use the deliverables outside of the scope or purpose of this agreement, all representations and warranties of KLERP TECH shall be void."],
                    [null, "Except for the express representations and warranties stated in this agreement, KLERP TECH makes no warranties whatsoever. KLERP TECH explicitly disclaims any other warranties of any kind, either express or implied, including but not limited to warranties of merchantability or fitness for a particular purpose or compliance with laws or government rules or regulations applicable to the project."],
                ],
            },
            {
                heading: "Indemnification and Liability",
                clauses: [
                    ["By Client:", "Client shall indemnify KLERP TECH from any and all damages, liabilities, costs, losses, expenses or attorney fees arising out of any claim, demand, or action by a third party arising out of any breach of Client's responsibilities or obligations, representations or warranties under this agreement. KLERP TECH shall promptly notify Client in writing of any third party claim or suit. Client shall have the right to fully control the defence and settlement of such claim or suit."],
                    ["By KLERP TECH:", "In the case of a third party lawsuit or proceeding based on a claim that deliverables breach the third party's intellectual property rights, and it is determined that such infringement has occurred, KLERP TECH may at its own expense, replace any infringing content with non-infringing content."],
                    ["Limitation of Liability:", "The services and the work product of KLERP TECH are sold 'as is'. In all circumstances, the maximum liability of KLERP TECH, its directors, officers, employees and affiliates to Client for damages for any and all causes whatsoever, and Client's maximum remedy, regardless of the form of action, whether in contract, tort or otherwise, shall be limited to the net profit of KLERP TECH. In no event shall KLERP TECH be liable for any lost data or content, lost profits, business interruption or for any indirect, incidental, special, consequential, exemplary or punitive damages arising out of or relating to the materials or the services provided by KLERP TECH, even if KLERP TECH has been advised of the possibility of such damages, and notwithstanding the failure of essential purpose of any limited remedy."],
                ],
            },
            {
                heading: "Term and Termination",
                clauses: [
                    ["Term:", "This agreement shall begin when both parties sign and shall continue until all services are complete and delivered, or until the agreement is terminated."],
                    ["Termination for Cause:", "Either party may terminate this agreement at any time, on thirty (30) days prior written notice if the other party breaches any of its material responsibilities or obligations under this agreement and fails to cure that breach during that thirty (30) day period."],
                    ["Termination by Mutual Agreement:", "This agreement may be terminated by the mutual agreement of the parties."],
                    ["Termination for Convenience:", "Either party may terminate this agreement at any time and for any reason on thirty (30) days prior written notice to the other party. If Client terminates the agreement under this section, KLERP TECH shall, at Client's reasonable discretion, complete any work assigned or scheduled during the notice period in accordance with the terms and conditions of this agreement."],
                    ["Termination Fees:", "In the event of termination, Client shall pay KLERP TECH for the services performed through the date of termination in the amount of a prorated portion of the fees due. Client shall pay all expenses, fees and additional costs incurred through the date of termination."],
                    ["Intellectual Property:", "If Client terminates and on full payment of compensation, KLERP TECH grants Client right and title as provided by this agreement with respect to those deliverables provided and accepted by Client as of the date of termination."],
                    ["Confidential Information:", "On expiration or termination of this agreement, (a) each party shall return or, at the disclosing party's request, destroy the confidential information of the other party, and (b) all rights and obligations regarding confidential information shall survive."],
                ],
            },
            {
                heading: "Rights to Final Work",
                clauses: [
                    ["License:", "KLERP TECH grants to Client a non-exclusive, perpetual and worldwide license to use and display the final deliverables in accordance with this agreement."],
                ],
            },
            {
                heading: "Rights to Other Deliverables",
                clauses: [
                    ["Preliminary Work:", "KLERP TECH retains all rights in and to all preliminary works. Client shall return all preliminary works to us within thirty (30) days of completion of the services."],
                ],
            },
            {
                heading: "Support Services",
                clauses: [
                    ["Warranty Period:", "During the first two (2) months following expiration of this agreement, KLERP TECH shall provide up to ten (10) hours of support services at no additional cost to Client per month. Support services means commercially reasonable technical support and assistance to maintain and update the deliverables, including correcting any errors or deficiencies. Requests for additional support will be billed on a time and materials basis at KLERP TECH's standard rate."],
                    ["Maintenance:", "After the warranty period expires and at Client's option, KLERP TECH is able to provide support services based on KLERP TECH's standard rate."],
                    ["No Enhancement:", "The services in the warranty period and maintenance do not include enhancements to the project or other services outside the scope of the proposal."],
                ],
            },
            {
                heading: "Enhancement",
                clauses: [
                    [null, "During maintenance, Client may request that KLERP TECH develop enhancements to the deliverables. KLERP TECH shall exercise commercially reasonable efforts to prioritise KLERP TECH's resources to create such enhancements. Client understands KLERP TECH may have preexisting obligations that may delay requested enhancements. KLERP TECH shall provide any enhancements at KLERP TECH's standard rate."],
                ],
            },
            {
                heading: "Dispute Resolution",
                clauses: [
                    ["Negotiation:", "Parties agree to attempt to resolve any dispute by negotiation between the parties."],
                    ["Arbitration/Mediation:", "If parties are unable to resolve the dispute by negotiation, either party may start mediation and/or binding arbitration in a forum mutually agreed to by the parties."],
                    ["Litigation:", "In all other circumstances, the parties specifically consent to the local, state and federal courts located in Malaysia."],
                    ["Attorney Fees:", "The prevailing party shall be entitled to recover its attorney's fees and costs in any dispute resolved by binding arbitration or litigation."],
                ],
            },
            {
                heading: "General",
                clauses: [
                    ["Modification/Waiver:", "Modifications to this agreement must be in writing and signed by both parties. Failure by either party to enforce any right or seek to remedy any breach under this agreement shall not be construed as a waiver of such rights nor shall a waiver by either party of default in one or more instances be construed as constituting a continuing waiver or as a waiver of any other breach."],
                    ["Notices:", "All notices under this agreement shall be given in writing either by: (a) Email; (b) Certified or registered mail, with return receipt requested. Notice will be effective when received, or in the case of email, on confirmation of receipt."],
                    ["No Assignment:", "Rights or obligations under this agreement shall not be transferred, assigned or encumbered without the prior written consent of the other party."],
                    ["Governing Law:", "This agreement shall be governed by the law of Malaysia."],
                    ["Severability:", "If any provision of this agreement is held invalid or unenforceable, the remainder of this agreement shall remain in full force and effect. Where possible the invalid or unenforceable provision shall be interpreted in such manner as to be effective and valid under applicable law."],
                    ["Headings:", "Headings and numbering used in this agreement are for convenience and reference only and shall not affect the scope, meaning, intent or interpretation of this agreement, and shall not have any legal effect."],
                    ["Complete Agreement:", "This agreement is the entire understanding of the parties and supersedes all prior understandings and documents relating to the subject matter of this agreement."],
                ],
            },
        ];

        letterTight = true;
        newLetterPage();
        drawLetterHeading("Terms & Conditions", true);
        paintLetterLines(drawLetterParagraph([{
            text: "All information in this proposal is subject to the following terms & conditions:",
            font: timesRoman,
            color: bodyInk,
        }]), letterLeft);

        termsSections.forEach((section) => {
            drawLetterHeading(section.heading);
            let clauseNumber = 1;
            (section.clauses || []).forEach(([lead, body]) => {
                drawLetterClause(clauseNumber, lead, body);
                clauseNumber += 1;
            });
            if (section.table) {
                letterY -= 12;
                drawLetterTable(section.table.headers, section.table.rows);
            }
            (section.clausesAfter || []).forEach(([lead, body]) => {
                drawLetterClause(clauseNumber, lead, body);
                clauseNumber += 1;
            });
        });

        const drawSignOff = (company, signatory) => {
            const blockHeight = signatory ? 200 : 160;
            if (letterY - blockHeight < letterBottom) newLetterPage();
            letterPage.drawText("Yours faithfully,", {
                x: letterLeft,
                y: letterY,
                size: letterSize,
                font: timesRoman,
                color: bodyInk,
            });
            letterY -= letterLeading;
            const companyLines = drawLetterParagraph([{
                text: String(company || "").trim() || "—",
                font: timesRomanBold,
                color: bodyInk,
            }], letterLeft);
            companyLines.forEach((parts) => {
                let x = letterLeft;
                parts.forEach((part) => {
                    letterPage.drawText(part.text, {
                        x,
                        y: letterY,
                        size: letterSize,
                        font: part.font,
                        color: part.color,
                    });
                    x += measureLetter(part.text, part.font);
                });
                letterY -= letterLeading;
            });
            letterY -= 52;
            letterPage.drawLine({
                start: { x: letterLeft, y: letterY },
                end: { x: letterLeft + 220, y: letterY },
                thickness: 0.8,
                color: bodyInk,
            });
            letterY -= 18;
            let titleBaseline = null;
            if (signatory) {
                const signatorySize = 10;
                letterPage.drawText(signatory.name, {
                    x: letterLeft,
                    y: letterY,
                    size: signatorySize,
                    font: timesRomanBold,
                    color: bodyInk,
                });
                letterY -= 14;
                titleBaseline = letterY;
                letterPage.drawText(signatory.title, {
                    x: letterLeft,
                    y: letterY,
                    size: signatorySize,
                    font: timesRoman,
                    color: bodyInk,
                });
                letterY -= 14;
            }
            letterY -= 16;
            return titleBaseline;
        };

        if (letterY - 260 < letterBottom) newLetterPage();
        letterY -= 18;
        letterPage.drawText("Thank you.", {
            x: letterLeft,
            y: letterY,
            size: letterSize,
            font: timesRoman,
            color: bodyInk,
        });
        letterY -= 32;
        const directorBaseline = drawSignOff("KLERP TECH SDN BHD", { name: "THEAN KHAI WYON", title: "DIRECTOR" });

        letterY -= 8;
        const dividerY = letterY;
        letterPage.drawLine({
            start: { x: letterLeft, y: dividerY },
            end: { x: letterRight, y: dividerY },
            thickness: 0.6,
            color: rgb(0.55, 0.57, 0.6),
        });
        const gapFromDirector = directorBaseline - dividerY;
        letterY = dividerY - gapFromDirector - letterSize * 0.66;

        letterPage.drawText("ACCEPTANCE", {
            x: letterLeft,
            y: letterY,
            size: letterSize,
            font: timesRoman,
            color: bodyInk,
        });
        letterY -= 32;
        paintLetterLines(drawLetterParagraph([{
            text: `We, ${customerName} hereby accept this project proposal and authorise KLERP TECH to begin work. We confirm our acceptance and compliance to all the terms and conditions stipulated in this proposal.`,
            font: timesRoman,
            color: bodyInk,
        }]), letterLeft);
        letterY -= 8;
        drawSignOff(String(customerName || "").toUpperCase());

        const pdfBytes = await pdfDoc.save();
        const fileName = `Proposal_${(order.invoiceId || order._id || "order").toString().replace(/\//g, "-")}_${moment().format("YYYY-MM-DD")}.pdf`;
        download(new Blob([pdfBytes]), fileName, "application/pdf");
    } catch (err) {
        console.error("createProposal error", err);
        throw err;
    }
};

export default createProposal;
